'use client'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import styled, { keyframes, css } from 'styled-components'
import { useLanguage } from '@/contexts/LanguageContext'
import { useConfig } from '@/contexts/ConfigContext'
import { KeyboardArrowDown, Code, Refresh, Star as StarIcon, Keyboard } from '@mui/icons-material'

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`

const scaleIn = keyframes`
    from { transform: scale(0.95); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
`

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(12px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    animation: ${fadeIn} 0.2s ease-out;
`

const ModalContainer = styled.div`
    position: relative;
    background: linear-gradient(
        143deg, #1d1d35ff 0%, #030710 100%);
    border: 1px solid rgba(0, 234, 255, 0.15);
    box-shadow: 
        0 0 20px rgba(0, 234, 255, 0.05),
        0 20px 60px rgba(0, 0, 0, 0.6),
        inset 0 1px 0 rgba(255, 255, 255, 0.05);
    padding: 30px;
    border-radius: 12px;
    width: 400px;
    max-width: 90%;
    animation: ${scaleIn} 0.2s ease-out;
    display: flex;
    flex-direction: column;
    gap: 20px;
    backdrop-filter: blur(10px);

    /* Corner brackets estilo cyber-tech */
    &::before,
    &::after {
        content: '';
        position: absolute;
        width: 20px;
        height: 20px;
        border: 2px solid #00eaff;
        opacity: 0.3;
    }

    &::before {
        top: -1px;
        left: -1px;
        border-right: none;
        border-bottom: none;
        border-top-left-radius: 12px;
    }

    &::after {
        bottom: -1px;
        right: -1px;
        border-left: none;
        border-top: none;
        border-bottom-right-radius: 12px;
    }
`

const Title = styled.h3`
    margin: 0;
    color: #ffffff;
    font-size: 1.75rem;
    font-weight: 600;
`

const Message = styled.p`
    margin: 0;
    color: #e0e0e0;
    font-size: 0.95rem;
    line-height: 1.6;
`

const Input = styled.input`
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(0, 234, 255, 0.15);
    color: #ffffff;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 1rem;
    width: 100%;
    outline: none;
    transition: all 0.3s ease;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.3);

    &:focus {
        border-color: rgba(0, 234, 255, 0.4);
        background: rgba(0, 0, 0, 0.5);
    }

    &::placeholder {
        color: rgba(255, 255, 255, 0.4);
    }
`

const TextArea = styled.textarea`
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(0, 234, 255, 0.15);
    color: #ffffff;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 0.9rem;
    width: 100%;
    min-height: 120px;
    outline: none;
    transition: all 0.3s ease;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.3);
    resize: vertical;
    font-family: monospace;

    &:focus {
        border-color: rgba(0, 234, 255, 0.4);
        background: rgba(0, 0, 0, 0.5);
    }

    &::placeholder {
        color: rgba(255, 255, 255, 0.4);
    }
`

const ButtonGroup = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 10px;
`

const Button = styled.button`
    padding: 11px 24px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.3s ease;
    border: none;
    outline: none;
    position: relative;
    overflow: hidden;

    /* Variant styles */
    background: ${({ $variant, $cancel }) =>
        $cancel ? 'transparent' :
            $variant === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                'linear-gradient(135deg, #00eaff 0%, #6366f1 100%)'
    };
    
    color: ${({ $variant, $cancel }) =>
        $cancel ? '#999' :
            '#000000ff'
    };
    
    border: 1px solid ${({ $variant, $cancel }) =>
        $cancel ? 'rgba(255, 255, 255, 0.15)' :
            $variant === 'danger' ? 'rgba(239, 68, 68, 0.3)' :
                'rgba(0, 234, 255, 0.2)'
    };

    box-shadow: ${({ $variant, $cancel }) =>
        $cancel ? 'none' :
            $variant === 'danger' ? '0 0 10px rgba(239, 68, 68, 0.15)' :
                '0 0 10px rgba(0, 234, 255, 0.15)'
    };

    &:hover {
        background: ${({ $variant, $cancel }) =>
        $cancel ? 'rgba(255, 255, 255, 0.08)' :
            $variant === 'danger' ? 'linear-gradient(135deg, #ff5555 0%, #ef4444 100%)' :
                'linear-gradient(135deg, #00f5ff 0%, #7c3aed 100%)'
    };
        transform: translateY(-2px);
        color: ${({ $variant, $cancel }) =>
        $cancel ? '#ddd' : '#ffffff'
    };
        box-shadow: ${({ $variant, $cancel }) =>
        $cancel ? 'none' :
            $variant === 'danger' ? '0 0 15px rgba(239, 68, 68, 0.25)' :
                '0 0 15px rgba(0, 234, 255, 0.25)'
    };
    }
    
    &:active {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none !important;
    }
`

const Label = styled.div`
    font-size: 0.85rem;
    color: #b0b0b0;
    font-weight: 500;
    margin-bottom: 4px;
`

const CustomSelectorWrapper = styled.div`
    position: relative;
    width: 100%;
`

const CustomSelectorButton = styled.div`
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(0, 234, 255, 0.15);
    border-radius: 8px;
    padding: 12px 14px;
    color: #fff;
    font-size: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: rgba(0, 0, 0, 0.5);
        border-color: rgba(0, 234, 255, 0.4);
    }

    ${({ $isOpen }) => $isOpen && css`
        border-color: #00eaff;
        background: rgba(0, 0, 0, 0.6);
        box-shadow: 0 0 15px rgba(0, 234, 255, 0.1);
    `}
`

const CustomSelectorDropdown = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 6px;
    background: #12141a;
    border: 1px solid rgba(0, 234, 255, 0.2);
    border-radius: 10px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.8);
    z-index: 1100;
    max-height: 250px;
    overflow-y: auto;
    animation: ${fadeIn} 0.15s ease-out;
    flex-direction: column;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(0, 234, 255, 0.2);
        border-radius: 2px;
    }
`

const CustomSelectorItem = styled.div`
    padding: 12px 16px;
    font-size: 13px;
    color: ${({ $active }) => ($active ? '#00eaff' : 'rgba(255, 255, 255, 0.7)')};
    background: ${({ $active }) => ($active ? 'rgba(0, 234, 255, 0.1)' : 'transparent')};
    cursor: pointer;
    transition: all 0.15s;
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;

    &:hover {
        background: rgba(255, 255, 255, 0.05);
        color: #fff;
    }

    svg {
        font-size: 16px;
        color: ${({ $active }) => ($active ? '#00eaff' : 'rgba(255, 255, 255, 0.3)')};
    }
`


export default function GlobalModal({ config, onClose }) {
    const { t } = useLanguage()
    const configContext = useConfig()
    const userConfigsFromContext = configContext?.userConfigs || []

    if (!config) return null

    // Prioritize userConfigs passed in the modal configuration, fallback to context
    const userConfigs = config.userConfigs || userConfigsFromContext

    // Default state
    const [inputValue, setInputValue] = useState(config.defaultValue || '')
    const [selectedTemplate, setSelectedTemplate] = useState('default') // default, empty, recommended
    const [isPresetOpen, setIsPresetOpen] = useState(false)

    const inputRef = useRef(null)
    const presetRef = useRef(null)

    const sortedUserConfigs = React.useMemo(() => {
        if (!userConfigs) return [];
        return userConfigs
            .filter(c => !c.isBuiltin)
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [userConfigs])

    useEffect(() => {
        if (!config) return

        // Sincronizar el valor del input con el valor por defecto del modal
        if (config.type === 'prompt' || config.type === 'create_config') {
            setInputValue(config.defaultValue || '')

            // Dar un pequeño respiro para que el DOM se actualice antes de enfocar
            const timer = setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus()
                    inputRef.current.select()
                }
            }, 50)
            return () => clearTimeout(timer)
        } else {
            setInputValue('')
        }

        if (config.type === 'create_config') {
            setSelectedTemplate('default')
            setIsPresetOpen(false)
        }
    }, [config])

    // Close on click outside for preset selector
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (presetRef.current && !presetRef.current.contains(event.target)) {
                setIsPresetOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleConfirm = useCallback((e) => {
        if (e && e.preventDefault) e.preventDefault()
        if (config.type === 'prompt') {
            onClose(inputValue)
        } else if (config.type === 'create_config') {
            if (!inputValue.trim()) return
            onClose({ name: inputValue, template: selectedTemplate })
        } else {
            onClose(true)
        }
    }, [config, inputValue, selectedTemplate, onClose])

    /* Keyboard shortcuts */
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose(null)
            // Enter confirmation for modals without inputs (Alert/Confirm)
            if (e.key === 'Enter' && config.type !== 'prompt' && config.type !== 'create_config') {
                handleConfirm()
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [config.type, handleConfirm, onClose])

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose(null) // Cancel
        }
    }


    return (
        <Overlay onClick={handleOverlayClick}>
            <ModalContainer as="form" onSubmit={handleConfirm}>
                <Title>{config.title}</Title>
                {config.message && <Message>{config.message}</Message>}

                {(config.type === 'prompt' || config.type === 'create_config') && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {config.type === 'create_config' && <Label>{t('CONFIG_NAME_LABEL')}</Label>}
                        {config.multiline ? (
                            <TextArea
                                ref={inputRef}
                                placeholder={config.placeholder}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                            />
                        ) : (
                            <Input
                                ref={inputRef}
                                placeholder={config.placeholder || t('MY_NEW_CONFIG_PLACEHOLDER')}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                            />
                        )}
                    </div>
                )}

                {config.type === 'create_config' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <Label>{t('SELECT_BASE')}</Label>
                        <CustomSelectorWrapper ref={presetRef}>
                            <CustomSelectorButton
                                $isOpen={isPresetOpen}
                                onClick={() => setIsPresetOpen(!isPresetOpen)}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {selectedTemplate === 'default' && <><Refresh style={{ fontSize: 18, color: '#00eaff' }} /> {t('TEMPLATE_DEFAULT')}</>}
                                    {selectedTemplate === 'empty' && <><Code style={{ fontSize: 18, color: '#00eaff' }} /> {t('TEMPLATE_EMPTY')}</>}
                                    {!['default', 'empty'].includes(selectedTemplate) && (
                                        <><Keyboard style={{ fontSize: 18, color: '#00eaff' }} /> {userConfigs.find(c => c.id === selectedTemplate)?.name || 'Custom'}</>
                                    )}
                                </div>
                                <KeyboardArrowDown style={{ fontSize: 20, opacity: 0.5 }} />
                            </CustomSelectorButton>

                            {isPresetOpen && (
                                <CustomSelectorDropdown>
                                    <CustomSelectorItem
                                        $active={selectedTemplate === 'default'}
                                        onClick={() => { setSelectedTemplate('default'); setIsPresetOpen(false); }}
                                    >
                                        <Refresh /> {t('TEMPLATE_DEFAULT')}
                                    </CustomSelectorItem>
                                    <CustomSelectorItem
                                        $active={selectedTemplate === 'empty'}
                                        onClick={() => { setSelectedTemplate('empty'); setIsPresetOpen(false); }}
                                    >
                                        <Code /> {t('TEMPLATE_EMPTY')}
                                    </CustomSelectorItem>

                                    {sortedUserConfigs.length > 0 && (
                                        <>
                                            <div style={{ width: '100%', padding: '10px 16px 5px', fontSize: '10px', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', fontWeight: 'bold', borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: '5px' }}>
                                                {t('MY_CONFIGS')}
                                            </div>
                                            {sortedUserConfigs.map(cfg => (
                                                <CustomSelectorItem
                                                    key={cfg.id}
                                                    $active={selectedTemplate === cfg.id}
                                                    onClick={() => { setSelectedTemplate(cfg.id); setIsPresetOpen(false); }}
                                                >
                                                    <Keyboard /> {cfg.name}
                                                </CustomSelectorItem>
                                            ))}
                                        </>
                                    )}
                                </CustomSelectorDropdown>
                            )}
                        </CustomSelectorWrapper>
                    </div>
                )}

                <ButtonGroup>
                    {config.cancelText && (
                        <Button type="button" $cancel onClick={() => onClose(null)}>
                            {config.cancelText}
                        </Button>
                    )}
                    <Button
                        type="submit"
                        $variant={config.variant}
                        disabled={config.type === 'create_config' && !inputValue.trim()}
                        style={{ opacity: (config.type === 'create_config' && !inputValue.trim()) ? 0.5 : 1 }}
                    >
                        {config.confirmText}
                    </Button>
                </ButtonGroup>
            </ModalContainer>
        </Overlay>
    )
}
